import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api, img, errMsg } from "../../lib/api";
import { WEDDING_STYLES, SHOP_CATEGORIES } from "../../lib/site";

const blank = (kind) => ({ kind, title: "", category: "", location: "", date: "", description: "", images: [], featured: false });

export const ProjectForm = ({ project, onClose, onSaved }) => {
  const [f, setF] = useState(blank("wedding"));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (project) setF({ ...blank(project.kind), ...project }); }, [project]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const cats = f.kind === "wedding" ? WEDDING_STYLES.map((s) => s.key) : SHOP_CATEGORIES;

  const upload = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd);
        urls.push(r.data.url);
      }
      setF((s) => ({ ...s, images: [...s.images, ...urls] }));
      toast.success(`${urls.length} foto caricate`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };
  const move = (i) => setF((s) => { const a = [...s.images]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; return { ...s, images: a }; });
  const drop = (i) => setF((s) => ({ ...s, images: s.images.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Inserisci un titolo");
    if (!f.images.length) return toast.error("Carica almeno una foto");
    setSaving(true);
    const { id, created_at, ...body } = f;
    try {
      if (id) await api.put(`/projects/${id}`, body);
      else await api.post("/projects", body);
      toast.success("Salvato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent data-testid="admin-project-form" className="max-w-2xl w-[94vw] max-h-[90vh] overflow-y-auto bg-cream border-stone">
        <DialogTitle className="font-serif text-3xl font-normal">{f.id ? "Modifica" : "Nuovo"} {f.kind === "wedding" ? "matrimonio" : "creazione"}</DialogTitle>
        <DialogDescription className="text-ash">La prima foto è la copertina.</DialogDescription>
        <div className="space-y-4 mt-2">
          <input data-testid="admin-form-title" className="field" placeholder="Titolo" value={f.title} onChange={set("title")} />
          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="admin-form-category" className="field" value={f.category} onChange={set("category")}>
              <option value="">{f.kind === "wedding" ? "Stile…" : "Categoria…"}</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="admin-form-location" className="field" placeholder="Luogo (es. Masseria, Ostuni)" value={f.location} onChange={set("location")} />
          </div>
          <input data-testid="admin-form-date" className="field" placeholder="Data (es. Giugno 2026)" value={f.date} onChange={set("date")} />
          <textarea data-testid="admin-form-description" rows={4} className="field resize-none" placeholder="Descrizione" value={f.description} onChange={set("description")} />
          <label className="flex items-center gap-2 text-sm text-ink"><input data-testid="admin-form-featured" type="checkbox" checked={f.featured} onChange={set("featured")} className="accent-[#535C48] w-4 h-4" /> In evidenza in home</label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {f.images.map((u, i) => (
              <div key={u + i} className="relative aspect-square bg-sand group">
                <img src={img(u)} alt="" className="w-full h-full object-cover" />
                {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] bg-cypress text-cream px-1.5 py-0.5">COPERTINA</span>}
                <button type="button" data-testid={`admin-form-remove-image-${i}`} onClick={() => drop(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-cream text-ink flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                {i > 0 && <button type="button" title="Sposta prima" onClick={() => move(i)} className="absolute top-1 left-1 w-6 h-6 rounded-full bg-cream text-ink flex items-center justify-center"><ArrowLeft className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
            <label data-testid="admin-form-upload" className="aspect-square border border-dashed border-cypress/50 flex flex-col items-center justify-center gap-1 text-xs text-cypress cursor-pointer hover:bg-sand">
              <Upload className="w-5 h-5" />{uploading ? "Caricamento…" : "Aggiungi foto"}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploading} onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-stone px-5 py-2.5 text-sm">Annulla</button>
            <button type="button" data-testid="admin-add-portfolio-submit" onClick={save} disabled={saving || uploading} className="rounded-full bg-cypress text-cream px-6 py-2.5 text-sm hover:bg-terra disabled:opacity-60">{saving ? "Salvataggio…" : "Salva"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
